import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";

/** 保存先をパネルごとに分けるための識別子 */
export type DesktopPanelLayoutKey =
  | "thread-create"
  | "reply"
  | "reply-simplified"
  | "mini-player";

export interface DesktopPanelLayout {
  width: number;
  height: number;
  left: number;
  top: number;
}

/**
 * 保存形式を変えたら上げる。version が一致しない保存値は破棄し、
 * 移行処理を持たずに既定サイズ・既定位置へ戻す。
 */
export const DESKTOP_PANEL_LAYOUT_VERSION = 1;

const STORAGE_PREFIX = "nijiurachan-desktop-panel-layout:";

export function desktopPanelLayoutStorageKey(
  key: DesktopPanelLayoutKey,
): string {
  return `${STORAGE_PREFIX}${key}`;
}

/**
 * 保存値を検証して返す。壊れたJSON・version不一致・非有限数・0以下のサイズは
 * すべて null を返し、呼び出し元は既定値へフォールバックする。
 * left/top は画面左外へ少しはみ出した位置も正当なため負値を許容する
 * (画面内へ収める判断は復元側の clamp が行う)。
 */
export function parseDesktopPanelLayout(
  storedValue: string | null,
): DesktopPanelLayout | null {
  try {
    const raw: unknown = JSON.parse(storedValue ?? "null");
    if (!isRecord(raw)) return null;
    if (raw.version !== DESKTOP_PANEL_LAYOUT_VERSION) return null;

    const width = toPositiveSize(raw.width);
    const height = toPositiveSize(raw.height);
    const left = toCoordinate(raw.left);
    const top = toCoordinate(raw.top);
    if (width === null || height === null || left === null || top === null) {
      return null;
    }
    return { width, height, left, top };
  } catch {
    return null;
  }
}

/**
 * getBoundingClientRect は小数を返すため、保存前に丸める。
 * 丸めないと操作のたびに保存文字列が変化し、subpixelのずれが蓄積する。
 */
export function serializeDesktopPanelLayout(
  layout: DesktopPanelLayout,
): string {
  return JSON.stringify({
    version: DESKTOP_PANEL_LAYOUT_VERSION,
    width: Math.round(layout.width),
    height: Math.round(layout.height),
    left: Math.round(layout.left),
    top: Math.round(layout.top),
  });
}

export function loadDesktopPanelLayout(
  key: DesktopPanelLayoutKey,
): DesktopPanelLayout | null {
  const stored = resolveSafePersistStorage().getItem(
    desktopPanelLayoutStorageKey(key),
  );
  return parseDesktopPanelLayout(typeof stored === "string" ? stored : null);
}

export function saveDesktopPanelLayout(
  key: DesktopPanelLayoutKey,
  layout: DesktopPanelLayout,
): void {
  resolveSafePersistStorage().setItem(
    desktopPanelLayoutStorageKey(key),
    serializeDesktopPanelLayout(layout),
  );
}

export function clearDesktopPanelLayout(key: DesktopPanelLayoutKey): void {
  resolveSafePersistStorage().removeItem(desktopPanelLayoutStorageKey(key));
}

function toPositiveSize(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function toCoordinate(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
